// Home.js
// -----------------------------------------------------------------------------
// NHV home page. Composed bottom-up from design-system primitives:
//   PageHero (welcome + service time + CTAs)
//     -> Section "Plan Your Visit" (real service time + address, no-pressure copy)
//     -> Section "Ways to Get Involved" (3 FeatureCards)
//     -> CtaBanner "Faithful Fellowship" (Connect -> /contact)
//
// This page is presentational: all content is static NHV info (service time,
// address, routes). No fetching, no global state. Buttons and links route via
// the design-system primitives, which handle safe navigation.
// -----------------------------------------------------------------------------

import * as React from 'react';
import Grid from '@mui/joy/Grid';

import ScheduleIcon from '@mui/icons-material/Schedule';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import GroupsIcon from '@mui/icons-material/Groups';

import {
  Section,
  SectionHeading,
  PageHero,
  PrimaryButton,
  FeatureCard,
  CtaBanner,
  InfoTile,
} from '../components/ui';
import CallToAction from '../components/CallToAction';
import usePageMeta from '../hooks/usePageMeta';

import nhv from '../assets/nhv-outside.jpg';
import WhatWeBelieve from '../assets/WhatWeBelieve.jpg';
import WhoWeAre from '../assets/WhoWeAre.jpg';
import HowToConnect from '../assets/HowToConnect.jpg';

// Module-private content constants. Static, render-only data for this page.
// Scope justification: kept module-local (not exported, not global mutable) so
// the JSX below stays declarative and the data has a single source of truth.
const GET_INVOLVED_CARDS = [
  {
    key: 'who-we-are',
    title: 'Who We Are',
    body: 'Get to know the people serving our church and shaping our vision.',
    image: WhoWeAre,
    imageAlt: 'Members of the New Horizon Vineyard community together',
    to: '/leaders',
  },
  {
    key: 'what-we-believe',
    title: 'What We Believe',
    body: 'Explore the core beliefs that shape our faith and guide our community.',
    image: WhatWeBelieve,
    imageAlt: 'An open Bible representing what we believe',
    to: '/beliefs',
  },
  {
    key: 'how-to-connect',
    title: 'How To Connect',
    body: "Whether you're new or looking to go deeper, there's a place for you here.",
    image: HowToConnect,
    imageAlt: 'People connecting and serving together',
    to: '/ministries',
  },
];

const MAP_URL = 'https://maps.app.goo.gl/aptJc46XDx7rMGwG7';

function Home() {
  usePageMeta(
    'New Horizon Vineyard',
    'New Horizon Vineyard is a church for all people and all ages in Rowlett, TX. ' +
      'Join us Sundays at 10:45 AM at 4301 Dalrock Road. Come as you are.',
    { exactTitle: true },
  );

  return (
    <>
      <PageHero
        image={nhv}
        height="lg"
        overlay="medium"
        align="center"
        eyebrow="Welcome to New Horizon Vineyard"
        title="We're Glad You're Here"
        subtitle="A community for all people and all ages. Join us this Sunday at 10:45 AM. Come as you are."
      >
        <CallToAction label="Plan a Visit" to="/join" />
        <PrimaryButton
          to="/sermons"
          color="neutral"
          sx={{
            bgcolor: 'common.white',
            color: 'primary.500',
            '&:hover': { bgcolor: 'neutral.200' },
            '&:active': { bgcolor: 'neutral.300' },
          }}
        >
          Watch Sermons
        </PrimaryButton>
      </PageHero>

      {/* Plan Your Visit: real NHV service time + address, no-pressure tone. */}
      <Section bg="tint" padding="md">
        <SectionHeading
          align="center"
          eyebrow="Plan Your Visit"
          title="What to Expect on Sunday"
          subtitle="No dress code, no pressure; just a warm welcome. Gather with us for worship, a practical message, and time for fellowship afterwards."
        />
        <Grid
          container
          spacing={4}
          sx={{ justifyContent: 'center', mt: 1 }}
        >
          <Grid xs={12} sm={6} md={4}>
            <InfoTile
              icon={<ScheduleIcon />}
              label="Sunday Service"
              value="10:45 AM every Sunday"
            />
          </Grid>
          <Grid xs={12} sm={6} md={4}>
            <InfoTile
              icon={<FmdGoodIcon />}
              label="Where to Find Us"
              value="4301 Dalrock Road, Rowlett, TX 75088"
              href={MAP_URL}
            />
          </Grid>
          <Grid xs={12} sm={6} md={4}>
            <InfoTile
              icon={<GroupsIcon />}
              label="Who It's For"
              value="All people, all ages: families welcome"
            />
          </Grid>
        </Grid>
      </Section>

      {/* Ways to Get Involved: 3 FeatureCards in a responsive grid. Smaller top
          padding so it reads as a continuation of the visit info above. */}
      <Section bg="default" padding="md" sx={{ pt: { xs: 4, md: 6 } }}>
        <SectionHeading
          align="center"
          eyebrow="Get Connected"
          title="Ways to Get Involved"
          subtitle="Whether you're new to our community or looking to deepen your involvement, there are many ways to get connected at New Horizon Vineyard, from small groups and volunteer teams to outreach and special events. We'd love to help you find your place."
        />
        <Grid container spacing={4} sx={{ justifyContent: 'center', mt: 1 }}>
          {GET_INVOLVED_CARDS.map((card) => (
            <Grid key={card.key} xs={12} sm={6} md={4}>
              <FeatureCard
                title={card.title}
                body={card.body}
                image={card.image}
                imageAlt={card.imageAlt}
                to={card.to}
                action={
                  <PrimaryButton to={card.to} variant="soft" size="sm">
                    Learn More
                  </PrimaryButton>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Faithful Fellowship: brand CTA band linking to the Contact page. */}
      <CtaBanner
        color="brand"
        align="center"
        eyebrow="Faithful Fellowship"
        title="Discover why the Vineyard could be the place you call HOME."
        text="Reach out and let us know you're coming, or ask us anything. We'd love to hear from you."
        action={
          <PrimaryButton to="/contact" color="accent">
            Connect
          </PrimaryButton>
        }
      />
    </>
  );
}

export default Home;
